import { Link, router, usePage } from '@inertiajs/react'
import { FileText, LayoutGrid, ListChecks, LogOut, Star, UserRoundKeyIcon } from 'lucide-react'
import { type ReactElement, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { track } from '@/lib/track'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import type { SharedData } from '@/types/shared'
import type { ProjectNav } from '@/types/project'

interface Props {
  children: ReactNode
  project?: ProjectNav
  mainClassName?: string
}

const ACCOUNT_TYPE_LABEL: Record<'guest' | 'standard' | 'demo', string> = {
  standard: 'Beta',
  demo: 'DEMO',
  guest: 'Guest'
}

export default function AppLayout ({ children, project, mainClassName }: Props): ReactElement {
  const { url, props } = usePage<SharedData>()
  const currentUser = props.current_user
  const publicFunnelEnabled = props.public_funnel_enabled
  const demoMode = props.demo_mode
  const accountTypeLabel = ACCOUNT_TYPE_LABEL[currentUser?.account_type ?? 'standard']

  const isProjectsActive = url === '/projects'
  const isSourceNotesActive = (project != null)
    ? url.startsWith(`/projects/${project.id}/source_notes`)
    : false
  const isSpecActive = (project != null)
    ? url.startsWith(`/projects/${project.id}/requirements_specification`) &&
      !url.includes('/requirements_quality')
    : false
  const isQualityActive = (project != null)
    ? url.startsWith(`/projects/${project.id}/requirements_quality`)
    : false

  const avatarLetter = currentUser?.email?.[0]?.toUpperCase() ?? '?'

  function handleLogout (): void {
    router.delete('/auth/session')
  }

  const navItemClass = (active: boolean): string =>
    cn(
      'flex items-center gap-[10px] px-[10px] py-2 rounded-md text-[13px] mb-0.5 transition-all',
      active
        ? 'bg-[rgba(53,71,193,0.25)] text-(--bp-panel-ink) font-medium'
        : 'text-(--bp-panel-ink2) hover:bg-white/[0.06] hover:text-(--bp-panel-ink)'
    )

  const navIconClass = (active: boolean): string =>
    cn('shrink-0', active ? 'opacity-100' : 'opacity-70')

  return (
    <div className='grid grid-cols-[240px_1fr] h-screen overflow-hidden'>
      <aside className='bg-(--bp-panel-bg) text-(--bp-panel-ink) flex flex-col overflow-y-auto border-r border-(--bp-panel-border)'>
        <div className='px-5 pt-[18px] pb-3 border-b border-(--bp-panel-border)'>
          <div className='flex items-center gap-2 text-[15px] font-bold text-(--bp-panel-ink) tracking-[-0.03em]'>
            <div className='w-[26px] h-[26px] bg-(--bp-accent) rounded-md flex items-center justify-center text-[11px] font-extrabold text-white'>
              BP
            </div>
            Blueprint
            <span
              className={cn(
                'px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide text-white',
                demoMode ? 'bg-amber-600' : 'bg-(--bp-accent)'
              )}
            >
              {accountTypeLabel}
            </span>
          </div>
        </div>

        <nav className='px-[10px] py-3 flex-1'>
          <div className='text-[10px] font-bold tracking-[0.1em] uppercase text-(--bp-panel-ink2) px-[10px] pt-2 pb-1 mt-1'>
            メニュー
          </div>
          <Link href='/projects' className={navItemClass(isProjectsActive)}>
            <LayoutGrid width={16} height={16} className={navIconClass(isProjectsActive)} />
            プロジェクト
          </Link>

          {(project != null) && (
            <>
              <div className='h-px bg-(--bp-panel-border) my-2 mx-[10px]' />
              <div className='text-[10px] font-bold tracking-[0.1em] uppercase text-(--bp-panel-ink2) px-[10px] pt-2 pb-1 mt-1'>
                {project.name}
              </div>
              <Link
                href={`/projects/${project.id}/source_notes`}
                className={navItemClass(isSourceNotesActive)}
              >
                <FileText width={16} height={16} className={navIconClass(isSourceNotesActive)} />
                要件ヒアリング
              </Link>
              <Link
                href={`/projects/${project.id}/requirements_specification`}
                className={navItemClass(isSpecActive)}
              >
                <ListChecks width={16} height={16} className={navIconClass(isSpecActive)} />
                要件定義
              </Link>
              <Link
                href={`/projects/${project.id}/requirements_quality`}
                className={navItemClass(isQualityActive)}
              >
                <Star width={16} height={16} className={navIconClass(isQualityActive)} />
                要件品質チェック
              </Link>
            </>
          )}
        </nav>

        {publicFunnelEnabled && (
          <div className='px-[10px] pb-3'>
            <a
              href='/contact'
              onClick={() => track('cta_click', 'sidebar_consult', { project_id: project?.id })}
              className='block px-3.5 py-3 rounded-lg text-[11px] font-bold text-white text-center leading-[1.5] no-underline'
              style={{ background: 'var(--bp-cta)' }}
            >
              システム開発、サービス開発の<br />ご相談はこちら →
            </a>
          </div>
        )}

        <div className='px-[10px] py-3 border-t border-(--bp-panel-border)'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className='flex items-center gap-[9px] w-full px-[10px] py-2 rounded-md cursor-pointer bg-transparent border-none hover:bg-white/[0.06]'>
                <div className='w-7 h-7 rounded-full bg-(--bp-accent) text-white text-[11px] font-bold flex items-center justify-center shrink-0'>
                  {avatarLetter}
                </div>
                <div className='flex-1 min-w-0 text-left'>
                  <div className='text-[11px] text-(--bp-panel-ink2) overflow-hidden text-ellipsis whitespace-nowrap'>
                    {currentUser?.email}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' side='top'>
              <DropdownMenuItem asChild>
                <Link href='/auth/passkeys/new' className='w-full'>
                  <UserRoundKeyIcon width={14} height={14} />
                  パスキーを登録
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant='destructive' onSelect={handleLogout}>
                <LogOut width={14} height={14} />
                ログアウト
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className='flex flex-col overflow-hidden bg-(--bp-bg)'>
        <div className={cn('flex-1 min-h-0', mainClassName ?? 'overflow-y-auto')}>
          {children}
        </div>

        <footer className='shrink-0 flex items-center justify-between gap-4 px-5 py-2 border-t border-(--bp-panel-border) bg-(--bp-panel-bg)'>
          <div className='text-[10px] text-(--bp-panel-ink2)'>© MountPosition, Inc.</div>
          {publicFunnelEnabled && (
            <div className='flex items-center gap-4 text-[11px] text-(--bp-panel-ink2)'>
              <a href='/terms' className='hover:text-(--bp-panel-ink)'>
                利用規約
              </a>
              <a href='/faq' className='hover:text-(--bp-panel-ink)'>
                よくある質問
              </a>
              <a href='/contact' className='hover:text-(--bp-panel-ink)'>
                お問い合わせ
              </a>
            </div>
          )}
        </footer>
      </main>
    </div>
  )
}
